import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ProcedureMoveAppBar from "../../../common/ProcedureMoveAppBar.js";
import Snackbar from "../../../common/Snackbar.js";
import { moveStep } from "../../../redux/stepSlice.js";
import { copyElement } from "../../../redux/sectionSlice.js";
import { generateSHA256 } from "../../../util/generateSHA256.js";
import RenderHtml from "../../../common/AppSpecificConpnents/PartsTable/RenderHtml.js";
import {
  HeadingWithTreePath,
  generateCompleteTreePath,
} from "../../SubassemblyDetails/utils/treePathUtils.js";
import { getPages, renderPreview, renderVideo } from "../plugins/utils.js";
import {
  applyTransparency,
  calculateGroupHeight,
  calculateTextBlockWidth,
  estimateKonvaTextHeight,
  formatListContent,
  getTextStyle,
  hasNoteContent,
  hasTaskContent,
  hasUserContent,
  measureHtmlHeight,
  stripHtmlPreserveFormatting,
} from "../plugins/utils.js";
import RectangleElement from "../../Canvas/components/rectangle/RectangleElement.jsx";
import ArrowElement from "../../Canvas/components/arrow/ArrowElement.jsx";
import CircleElement from "../../Canvas/components/circle/CircleElement.jsx";
import LineElement from "../../Canvas/components/line/LineElement.jsx";
import CanvasImage from "../../Canvas/components/canvas-core/CanvasImage.jsx";
import TextElement from "../../Canvas/components/text/TextElement.jsx";
import DoubleSidedArrowElement from "../../Canvas/components/arrow/DoubleSidedArrowElement.jsx";
import CanvasDisplay from "../../Canvas/components/canvas-core/CanvasDisplay.jsx";

import {
  extractVersionStructure,
  getAuditTrailData,
} from "../../../util/getAuditTrailData.js";
import { getRevAllNodes } from "../../../redux/procedureRevisionSlice.js";

const ProcedureStepMovePreview = () => {
  const { sections } = useSelector((state) => state.section);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  let richTextCounter = 1;
  let canvasCounter = 1;
  let videoCounter = 1;
  let formCounter = 1;
  let substepCounter = 1;
  let stepCounter = 1;
  const [isLoading, setIsLoading] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [auditData, setAuditData] = useState({
    user_ref: localStorage.getItem("user_id") || "",
    ip_address: "",
    device_info: navigator.userAgent,
    hash: generateSHA256(Math.random().toString()),
    signature: "",
  }); // Fetch IP address and generate hash
  let richTextsCounter = {};
  let attachmentCounter = 1;
  const [isModalOpen, setModalOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });
  const {
    stepsBySection,
    substepsByStep,
    richTexts,
    attachments,
    forms,
    canvases,
    videos,
  } = useSelector((state) => state.step);

  const [selectedView, setSelectedView] = useState("new");
  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) =>
        setAuditData((prev) => ({ ...prev, ip_address: data.ip }))
      )
      .catch((err) => console.error("Failed to fetch IP:", err));
  }, []);

  const {
    moveType,
    sourceSectionIndex,
    targetSectionIndex,
    revision,
    partNumber,
    type,
    sourceStepIndex,
    status,
    steps,
    targetStepIndex = 0, // Default value if not provided
    targetSectionData,
    fromSectionId,
    partId,
    revisionId,
    fromStepId,
    fromStepTreeId,
    fromStepSiblingOrder,
    createCopy = false, // Add createCopy from location.state
  } = location.state;
  const [previewSections, setPreviewSections] = useState([...sections]);
  const sortedSections = [...sections];
  const { allRevisionNodes } = useSelector((state) => state.procedureRevision);
  useEffect(() => {
    if (fromStepId) {
      dispatch(
        getRevAllNodes({
          partId: partId,
          revisionId: revisionId,
        })
      );
    }
  }, [dispatch, fromStepId, partId, revisionId]);

  useEffect(() => {
    // Create preview of the move
    const newSections = [...sections];

    if (location.state.movingStep) {
      // For step moves, we'll calculate the step positions in the render based on the updated data
      // No need to modify sections array

      // Get the source section ID
      const sourceSectionId = sortedSections.find(
        (s) => s.section_tree_node_id === sourceSectionIndex
      )?.section_tree_node_id;
      // Get the target section ID
      const targetSectionId =
        sortedSections[targetSectionIndex]?.section_tree_node_id;
      // Check if moving within the same section
      const isSameSection = sourceSectionId === targetSectionId;
    } else {
      // Original section move logic
      const sourceSection = newSections[sourceSectionIndex];
      newSections.splice(sourceSectionIndex, 1);
      newSections.splice(targetSectionIndex, 0, sourceSection);
      setPreviewSections(newSections);
    }
  }, [
    sourceSectionIndex,
    targetSectionIndex,
    moveType,
    sourceStepIndex,
    location.state.targetStepIndex,
    location.state.movingStep,
    createCopy, // Add createCopy to dependencies
  ]);

  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleConfirm = () => {
    setModalOpen(true);
  };

  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    return intA - intB;
  };

  const handleConfirmSave = async () => {
    if (!comment.trim()) {
      showSnackbar("Please enter a comment before confirming.", "error");
      return;
    }

    setIsModalLoading(true);
    try {
      if (!comment) {
        showSnackbar("Comment is mandatory.", "error");
        return;
      }

      const sourceSectionId = fromSectionId;
      // Get the target section ID
      const targetSectionId = targetSectionData?.sectionId;
      // Check if moving within the same section
      const isSameSection = sourceSectionId === targetSectionId;

      // Get source and target steps
      const sourceSteps = stepsBySection[sourceSectionId] || [];
      const targetSteps = stepsBySection[targetSectionId] || [];

      // Find the specific steps
      const sourceStep = sourceSteps.find(
        (step) => step.step_tree_node_id === fromStepId
      );
      const targetStep = targetSteps.find(
        (step) => step.step_tree_node_id === targetSectionData.stepId
      );

      // Get step numbers from title or state if available
      // The title format is typically "Step X" where X is the step number
      let sourceStepNumber = null;
      let targetStepNumber = null;

      if (location.state.title) {
        const titleMatch = location.state.title.match(/Step\s+(\d+)/i);
        if (titleMatch && titleMatch[1]) {
          sourceStepNumber = parseInt(titleMatch[1], 10);
        }
      }

      if (targetSectionData && targetSectionData.globalStepNumber) {
        targetStepNumber = targetSectionData.globalStepNumber;
      }

      // Get the actual sibling orders for steps within their sections
      // These are the values that matter for ordering within a section
      const sourceSiblingOrder =
        sourceStep?.sibling_order ||
        sourceStep?.procedure_tree?.sibling_order ||
        fromStepSiblingOrder;

      const targetSiblingOrder =
        targetStep?.sibling_order ||
        targetStep?.procedure_tree?.sibling_order ||
        targetSectionData.stepSiblingOrder;

      // Extract sibling orders as numbers (not strings)
      const sourceStepOrder = Number(
        sourceStep?.sibling_order ||
          sourceStep?.procedure_tree?.sibling_order ||
          fromStepSiblingOrder ||
          sourceStepNumber ||
          0
      );
      const targetStepOrder = Number(
        targetStep?.sibling_order ||
          targetStep?.procedure_tree?.sibling_order ||
          targetSectionData.stepSiblingOrder ||
          targetStepNumber ||
          0
      );

      const action = location.state.movingStep ? "MOVE_STEP" : "MOVE_SECTION";
      let actionComment;

      if (location.state.movingStep) {
        const actionVerb = createCopy ? "Copied" : "Moved";
        if (moveType === "into") {
          actionComment = isSameSection
            ? `${actionVerb} Step ${sourceStepIndex + 1}`
            : `${actionVerb} Step ${sourceStepIndex + 1}`;
        } else if (moveType === "up") {
          actionComment = isSameSection
            ? `${actionVerb} Step ${sourceStepIndex + 1} above Step ${
                location.state.targetStepIndex + 1
              } within same Section ${targetSectionIndex + 1}`
            : `${actionVerb} Step ${sourceStepIndex + 1} above Step ${
                location.state.targetStepIndex + 1
              } in Section ${targetSectionIndex + 1}`;
        } else {
          actionComment = isSameSection
            ? `${actionVerb} Step ${sourceStepIndex + 1} below Step ${
                location.state.targetStepIndex + 1
              } within same Section ${targetSectionIndex + 1}`
            : `${actionVerb} Step ${sourceStepIndex + 1} below Step ${
                location.state.targetStepIndex + 1
              } in Section ${targetSectionIndex + 1}`;
        }
      } else {
        const actionVerb = createCopy ? "Copied" : "Moved";
        actionComment = `${actionVerb} Section ${sourceSectionIndex + 1} ${
          moveType === "up" ? "above" : "below"
        } Section ${targetSectionIndex + 1}`;
      }
      let action_to_show = "";

      if (moveType === "into") {
        action_to_show = `${createCopy ? "Copied" : "Moved"} Step ${
          sourceStepIndex + 1
        } in Section ${targetSectionIndex + 1}`;
      } else {
        action_to_show = `${createCopy ? "Copied" : "Moved"} Step ${
          sourceStepIndex + 1
        } ${moveType === "up" ? "above" : "below"} Step ${
          targetSectionData?.globalStepNumber ||
          targetSectionData?.stepIndex + 1 ||
          location.state.targetStepIndex + 1
        } in Section ${targetSectionIndex + 1}`;
      }

      action_to_show += ` in ${partNumber}, Rev ${revision}`;
      let parent_version = Object.values(stepsBySection)
        ?.flat()
        .find((step) => step.step_tree_node_id === fromStepId);

      let newContent = structuredClone(allRevisionNodes);
      const cursection = allRevisionNodes.find(
        (s) => s.section_id_ref === fromSectionId
      );
      const currentStep =
        cursection?.steps.find((s) => s.step_id_ref === fromStepId) || {};

      // Manage New Content After Move/Copy
      if (createCopy) {
        // For copy operations, keep the original step in place and add a copy
        if (moveType === "into") {
          if (fromSectionId === targetSectionId) {
            // Copy within same section - add copy at the end
            newContent = newContent.map((section) => {
              if (section.section_id_ref === fromSectionId) {
                return {
                  ...section,
                  steps: [...section.steps, currentStep],
                };
              }
              return section;
            });
          } else {
            // Copy to different section - add copy to target section
            newContent = newContent.map((section) => {
              if (section.section_id_ref === targetSectionId) {
                return {
                  ...section,
                  steps: [...section.steps, currentStep],
                };
              }
              return section;
            });
          }
        } else if (moveType === "up" || moveType === "below") {
          if (fromSectionId === targetSectionId) {
            // Copy within same section - insert copy at target position
            newContent = newContent.map((section) => {
              if (section.section_id_ref === fromSectionId) {
                const steps = [...section.steps];
                const index = steps.findIndex(
                  (step) => step.step_id_ref === targetSectionData.stepId
                );
                if (index !== -1) {
                  if (moveType === "up" && index === 0) {
                    steps.unshift(currentStep);
                  } else {
                    steps.splice(
                      moveType === "up" ? index : index + 1,
                      0,
                      currentStep
                    );
                  }
                }
                return { ...section, steps };
              }
              return section;
            });
          } else {
            // Copy to different section - insert copy at target position
            newContent = newContent.map((section) => {
              if (section.section_id_ref === targetSectionId) {
                const steps = [...section.steps];
                const index = steps.findIndex(
                  (step) => step.step_id_ref === targetSectionData.stepId
                );
                if (index !== -1) {
                  if (moveType === "up" && index === 0) {
                    steps.unshift(currentStep);
                  } else {
                    steps.splice(
                      moveType === "up" ? index : index + 1,
                      0,
                      currentStep
                    );
                  }
                }
                return { ...section, steps };
              }
              return section;
            });
          }
        }
      } else {
        // Original move logic (not copy)
        if (moveType === "into") {
          if (fromSectionId === targetSectionId) {
            newContent = newContent.map((section) => {
              if (section.section_id_ref === fromSectionId) {
                return {
                  ...section,
                  steps: [
                    ...section.steps.filter(
                      (step) => step.step_id_ref !== fromStepId
                    ),
                    currentStep,
                  ],
                };
              }
              return section;
            });
          } else {
            newContent = newContent.map((section) => {
              if (section.section_id_ref === fromSectionId) {
                return {
                  ...section,
                  steps: [
                    ...section.steps.filter(
                      (step) => step.step_id_ref !== fromStepId
                    ),
                  ],
                };
              } else if (section.section_id_ref === targetSectionId) {
                return {
                  ...section,
                  steps: [...section.steps, currentStep],
                };
              }
              return section;
            });
          }
        } else if (moveType === "up" || moveType === "below") {
          if (fromSectionId === targetSectionId) {
            newContent = newContent.map((section) => {
              if (section.section_id_ref === fromSectionId) {
                const steps = section.steps.filter(
                  (s) => s.step_id_ref !== fromStepId
                );
                const index = steps.findIndex(
                  (step) => step.step_id_ref === targetSectionData.stepId
                );
                if (index !== -1) {
                  if (moveType === "up" && index === 0) {
                    steps.unshift(currentStep);
                  } else {
                    steps.splice(
                      moveType === "up" ? index : index + 1,
                      0,
                      currentStep
                    ); // insert after target
                  }
                }
                return { ...section, steps };
              }
              return section;
            });
          } else {
            newContent = newContent.map((section) => {
              if (section.section_id_ref === fromSectionId) {
                return {
                  ...section,
                  steps: section.steps.filter(
                    (s) => s.step_id_ref !== fromStepId
                  ),
                };
              } else if (section.section_id_ref === targetSectionId) {
                const index = section.steps.findIndex(
                  (step) => step.step_id_ref === targetSectionData.stepId
                );
                if (index !== -1) {
                  if (moveType === "up" && index === 0) {
                    section.steps.unshift(currentStep);
                  } else {
                    section.steps.splice(
                      moveType === "up" ? index : index + 1,
                      0,
                      currentStep
                    ); // insert after target
                  }
                }
              }
              return section;
            });
          }
        }
      }

      const audit_trail_data = await getAuditTrailData(
        {
          action: createCopy ? "STEP_COPIED" : "STEP_MOVED",
          comment: comment,
          parent_version: parent_version ? parent_version.version_id_ref : null,
          version_content: {
            action_to_show,
            originingalContent: extractVersionStructure(allRevisionNodes),
            newContent: extractVersionStructure(newContent),
            moveType,
            rev: revision,
            status,
            type,
            item_code_type: type,
            partNumber,
            move_copy_action: `Section ${
              targetSectionIndex + 1
            } in ${partNumber}, Rev ${revision}`,
          },
        },
        false
      );

      // Create the base payload with values that are common to all move types
      const payload = {
        from_procedure_tree_node_ref: fromStepTreeId,
        from_type: "step",
        from_part_id_ref: partId,
        from_procedure_rev_ref: revisionId,
        from_section_id_ref: sourceSectionId,
        from_step_id_ref: fromStepId,
        from_substep_id_ref: null,
        to_part_id_ref: partId,
        to_procedure_rev_ref: revisionId,
        to_section_id_ref: targetSectionId,
        to_substep_id_ref: null,
        ...(createCopy ? {} : { create_copy: createCopy }), // Only add create_copy for move operations
        audit_trail_data,
      };

      // Set specific values based on move type
      if (moveType === "into") {
        // For "Move Into", we're moving to a section, not a specific step
        payload.to_procedure_tree_node_ref = targetSectionData.sectionTreeId;
        payload.to_type = "step";
        payload.to_step_id_ref = null;
        payload.direction = createCopy ? "paste_down" : "move_down";
        payload.direction_move_into = true;

        // Use the source step's order for from_order
        payload.from_order = sourceStepOrder;

        // section 1
        // step 1 -> 1

        // section 2

        // For to_order, find the max sibling order in the target section and add 1
        const allOrders = targetSteps
          .map((step) =>
            Number(
              step.sibling_order || step.procedure_tree?.sibling_order || 0
            )
          )
          .filter((order) => !isNaN(order));

        payload.to_order =
          allOrders.length > 0 ? Math.max(...allOrders) + 1 : 1;

      } else {
        // For "Move Up" or "Move Below", we're moving relative to a specific step
        payload.to_procedure_tree_node_ref = targetSectionData.stepTreeId;
        payload.to_type = "step";
        payload.to_step_id_ref = targetSectionData.stepId;
        payload.direction_move_into = false;

        // Check if we're moving within the same section
        if (isSameSection) {
          // Find the indices of source and target steps
          const sourceIndex = sourceSteps.findIndex(
            (s) => s.step_tree_node_id === fromStepId
          );
          const targetIndex = targetSteps.findIndex(
            (s) => s.step_tree_node_id === targetSectionData.stepId
          );

          // Get the actual source and target steps
          const sourceStep = sourceSteps.find(
            (s) => s.step_tree_node_id === fromStepId
          );
          const targetStep = targetSteps.find(
            (s) => s.step_tree_node_id === targetSectionData.stepId
          );

          // Get the actual sibling orders if available
          const sourceOrder =
            sourceStep?.sibling_order ||
            sourceStep?.procedure_tree?.sibling_order ||
            fromStepSiblingOrder ||
            sourceStepNumber ||
            null;

          const targetOrder =
            targetStep?.sibling_order ||
            targetStep?.procedure_tree?.sibling_order ||
            targetSectionData.stepSiblingOrder ||
            targetStepNumber ||
            null;

          // Always use the original sibling orders for same-section moves
          // This is more reliable than calculating from array indices
          if (sourceOrder !== null && !isNaN(Number(sourceOrder))) {
            payload.from_order = Number(sourceOrder);
          } else if (
            fromStepSiblingOrder &&
            !isNaN(Number(fromStepSiblingOrder))
          ) {
            // If we have the original sibling order from location state, use it
            payload.from_order = Number(fromStepSiblingOrder);
          } else if (sourceIndex !== -1) {
            // As a last resort, calculate based on index
            payload.from_order = sourceIndex + 1;
          } else {
            // Fallback value
            payload.from_order = 1;
          }

          if (moveType === "up") {
            // For move up operations, use the actual target sibling order
            if (targetOrder !== null && !isNaN(Number(targetOrder))) {
              payload.to_order = Number(targetOrder);
            } else if (targetIndex !== -1) {
              // As a last resort, calculate based on index
              payload.to_order = targetIndex + 1;
            } else {
              // Fallback value
              payload.to_order = 1;
            }

            // Ensure to_order is never greater than from_order for "move up"
            if (payload.to_order > payload.from_order) {
              // If somehow to_order is greater, use a value less than from_order
              payload.to_order = Math.max(1, payload.from_order - 1);
            }

            payload.direction = createCopy ? "paste_up" : "move_up";
          } else {
            // For move below operations
            if (targetOrder !== null && !isNaN(Number(targetOrder))) {
              payload.to_order = Number(targetOrder) + 1;
            } else if (targetIndex !== -1) {
              payload.to_order = targetIndex + 2;
            } else {
              // Fallback value
              payload.to_order = payload.from_order + 1;
            }

            // Ensure to_order is never less than from_order for "move down"
            if (payload.to_order < payload.from_order) {
              payload.to_order = payload.from_order + 1;
            }

            payload.direction = createCopy ? "paste_down" : "move_down";
          }

          // For debugging
        } else {
          // Original code for cross-section moves
          if (moveType === "up") {
            // For "Move Up", we need both orders in the payload
            payload.direction = createCopy ? "paste_up" : "move_up";
            payload.from_order = sourceStepOrder || 1; // Ensure 1-based indexing
            payload.to_order = targetStepOrder || 1; // Ensure 1-based indexing

          } else {
            // For "Move Below"
            payload.direction = createCopy ? "paste_down" : "move_down";
            payload.from_order = sourceStepOrder || 1; // Ensure 1-based indexing
            payload.to_order = (targetStepOrder || 1) + 1; // Add 1 to place it below the target step

          }
        }
      }

      // Special handling for cross-section moves
      if (sourceSectionId !== targetSectionId) {
        // CRITICAL: For cross-section moves, set specific values that will work reliably
        payload.direction_move_into = true;

        if (moveType === "up") {
          payload.direction = createCopy ? "paste_up" : "move_up";
          payload.from_order = sourceStepOrder || 1; // Use 1-based indexing
          payload.to_order = 1; // Use 1 for to_order when moving up (1-based indexing)
        } else if (moveType === "below") {
          payload.direction = createCopy ? "paste_down" : "move_down";
          payload.from_order = sourceStepOrder || 1; // Use 1-based indexing

          // Instead of using 999, calculate the position right after the target step
          if (targetSectionData && targetSectionData.stepId) {
            // Find the target step's index
            const targetIndex = targetSteps.findIndex(
              (s) => s.step_tree_node_id === targetSectionData.stepId
            );

            if (targetIndex !== -1) {
              // Convert to 1-based and add 1 to place below
              payload.to_order = targetIndex + 2;
            } else {
              // Fallback to the sibling order from the target data
              payload.to_order = (targetSectionData.stepSiblingOrder || 0) + 1;
            }
          } else {
            // If we don't have target step data, use a reasonable value
            // Find the first step in target section
            if (targetSteps && targetSteps.length > 0) {
              // Get the first step's order and place after it
              const firstStepOrder =
                targetSteps[0].sibling_order ||
                targetSteps[0].procedure_tree?.sibling_order ||
                1;
              payload.to_order = Number(firstStepOrder) + 1;
            } else {
              // No steps in target section, use 1
              payload.to_order = 1;
            }
          }
        } else if (moveType === "into") {
          payload.direction = createCopy ? "paste_down" : "move_down";
          payload.from_order = sourceStepOrder || 1; // Use 1-based indexing

          // Calculate a proper "to_order" for "into" moves
          // Find the max sibling order in the target section and add 1
          if (targetSteps && targetSteps.length > 0) {
            const allOrders = targetSteps
              .map((step) =>
                Number(
                  step.sibling_order || step.procedure_tree?.sibling_order || 0
                )
              )
              .filter((order) => !isNaN(order));

            payload.to_order =
              allOrders.length > 0 ? Math.max(...allOrders) + 1 : 1;

          } else {
            // No steps in target section, use 1
            payload.to_order = 1;
          }
        }

        // No need for additional processing of cross-section moves
        // Skip ahead to final validation
      } else {
        // Ensure all numeric values are properly parsed
        payload.from_order = Number(payload.from_order);
        payload.to_order = Number(payload.to_order);

        // Make sure we don't send NaN values
        if (isNaN(payload.from_order)) payload.from_order = 0;
        if (isNaN(payload.to_order)) payload.to_order = 0;

        // CRITICAL: Ensure we NEVER have the same value for from_order and to_order
        // This is a common cause of issues with the move operation
        if (payload.from_order === payload.to_order) {
          // If they're the same, adjust to_order based on direction
          if (
            payload.direction === "move_up" ||
            payload.direction === "paste_up"
          ) {
            payload.to_order = Math.max(0, payload.from_order - 1);
          } else {
            payload.to_order = payload.from_order + 1;
          }
        }

        // Set reasonable bounds on the values
        if (payload.to_order < 0) payload.to_order = 0;
        if (payload.to_order > 1000) payload.to_order = 1000;
        if (payload.from_order < 0) payload.from_order = 0;
        if (payload.from_order > 1000) payload.from_order = 1000;

        // Final sanity check for extreme values that might indicate a calculation error
        // Prevent using 999 or 1000 which are often our fallback values
        if (payload.from_order >= 999 || isNaN(payload.from_order)) {
          // For same-section moves, prioritize using the sibling order
          if (sourceSectionId === targetSectionId) {
            const sourceSiblingOrder =
              sourceStep?.sibling_order ||
              sourceStep?.procedure_tree?.sibling_order;
            if (sourceSiblingOrder && !isNaN(Number(sourceSiblingOrder))) {
              payload.from_order = Number(sourceSiblingOrder);
            } else {
              // Try to get a reasonable value from the step number, sibling order, or use 1
              payload.from_order = sourceStepNumber || sourceStepOrder || 1;
            }
          } else {
            // Try to get a reasonable value from the step number, sibling order, or use 1
            payload.from_order = sourceStepNumber || sourceStepOrder || 1;
          }
        }

        if (payload.to_order >= 999 || isNaN(payload.to_order)) {
          // For same-section moves, prioritize using the sibling order
          if (sourceSectionId === targetSectionId) {
            const targetSiblingOrder =
              targetStep?.sibling_order ||
              targetStep?.procedure_tree?.sibling_order;

            if (
              moveType === "up" &&
              targetSiblingOrder &&
              !isNaN(Number(targetSiblingOrder))
            ) {
              // For move up within same section, use target's sibling_order
              payload.to_order = Number(targetSiblingOrder);
              // Ensure to_order is less than from_order for "move up"
              if (payload.to_order >= payload.from_order) {
                // For move up, to_order must be less than from_order
                // Use a position slightly before the target
                payload.to_order = Math.max(
                  0.5,
                  Number(targetSiblingOrder) - 0.5
                );
              }
            } else if (
              moveType === "below" &&
              targetSiblingOrder &&
              !isNaN(Number(targetSiblingOrder))
            ) {
              // For move below, use sibling_order + 0.5
              payload.to_order = Number(targetSiblingOrder) + 0.5;
            } else if (targetStepNumber) {
              // Use the target step number if available
              if (moveType === "up") {
                payload.to_order = Math.max(
                  0.5,
                  Number(targetStepNumber) - 0.5
                );
              } else {
                payload.to_order = Number(targetStepNumber) + 0.5;
              }
            } else {
              // Fallback based on from_order
              if (moveType === "up") {
                payload.to_order = Math.max(
                  0.5,
                  Number(payload.from_order) - 0.5
                );
              } else {
                payload.to_order = Number(payload.from_order) + 0.5;
              }
            }
          } else {
            // Cross-section move
            if (moveType === "up") {
              // For move up, use the target step number directly if available
              if (targetStepNumber) {
                payload.to_order = Math.max(
                  0.5,
                  Number(targetStepNumber) - 0.5
                );
              } else {
                // Otherwise ensure to_order is less than from_order
                payload.to_order = Math.max(
                  0.5,
                  Number(payload.from_order) - 0.5
                );
              }
            } else {
              // For move below or into
              if (targetStepNumber) {
                payload.to_order = Number(targetStepNumber) + 0.5;
              } else {
                // Ensure to_order is greater than from_order
                payload.to_order = Number(payload.from_order) + 0.5;
              }
            }
          }
        }
      }

      // Ensure from_order and to_order are strings in the payload (API requirement)
      payload.from_order = Number(payload.from_order);
      payload.to_order = Number(payload.to_order);

      // Log the payload for debugging
      if (createCopy) {
        // Use copyElement action for copy operations
        await dispatch(copyElement(payload)).unwrap();
      } else {
        // Use moveStep action for move operations
        await dispatch(moveStep(payload)).unwrap();
      }
      showSnackbar(
        location.state.movingStep
          ? createCopy
            ? "Step copied successfully!"
            : "Step moved successfully!"
          : createCopy
          ? "Section copied successfully!"
          : "Section moved successfully!",
        "success"
      );
      setModalOpen(false);
      navigate(-3); // Navigate back to the previous page
    } catch (error) {
      console.error("Error during move operation:", error);
      showSnackbar(
        `Error ${createCopy ? "copying" : "moving"} step: ` + error.message,
        "error"
      );
    } finally {
      setIsModalLoading(false);
    }
  };

  const getReorderedStepsBySection = (
    stepsBySection,
    sortedSections,
    sourceSectionIndex,
    targetSectionIndex,
    sourceStepIndex,
    targetStepIndex,
    moveType,
    movingStep = false,
    createCopy = false
  ) => {
    if (selectedView === "original") return stepsBySection;

    // Clone the existing structure to avoid mutating state directly
    const updatedStepsBySection = JSON.parse(JSON.stringify(stepsBySection));

    if (movingStep) {
      // Get the source section ID
      const sourceSectionId = location.state.fromSectionId;
      if (!sourceSectionId) return updatedStepsBySection;

      // Get the target section ID
      const targetSectionId = targetSectionData.sectionId;
      if (!targetSectionId) return updatedStepsBySection;

      // Get the source steps array and the step to move
      const sourceSteps = [...(updatedStepsBySection[sourceSectionId] || [])];
      if (!sourceSteps.length) return updatedStepsBySection;

      // Find the step to move by its ID instead of index
      const stepToMoveIndex = sourceSteps.findIndex(
        (step) => step.step_tree_node_id === location.state.fromStepId
      );
      if (stepToMoveIndex === -1) return updatedStepsBySection;

      // Get the step to move/copy
      const stepToMove = sourceSteps[stepToMoveIndex];

      if (createCopy) {
        // For copy operations, keep the original step in place and add a copy
        // Make a copy of the target steps array
        let targetSteps = [...(updatedStepsBySection[targetSectionId] || [])];

        // Sort the target steps by sibling_order to understand their positions
        const sortedTargetSteps = [...targetSteps].sort((a, b) => {
          const aOrder = parseInt(
            a.sibling_order || a.procedure_tree?.sibling_order || 0
          );
          const bOrder = parseInt(
            b.sibling_order || b.procedure_tree?.sibling_order || 0
          );
          return aOrder - bOrder;
        });

        // Find the target step in the sorted array
        const targetStep = targetSteps.find(
          (step) => step.step_tree_node_id === targetSectionData.stepId
        );

        // Find the index of the target step in the original (unsorted) array
        const targetStepIndex = targetSteps.findIndex(
          (step) => step.step_tree_node_id === targetSectionData.stepId
        );

        // Find the position of the target step in the sorted array
        const targetStepSortedIndex = sortedTargetSteps.findIndex(
          (step) => step.step_tree_node_id === targetSectionData.stepId
        );

        // Calculate the insertion index based on move type
        let insertIndex;

        if (moveType === "into") {
          // Copy to end of target section
          insertIndex = targetSteps.length;
        } else if (moveType === "up") {
          // For "Move Up", insert at the target step's position
          if (targetStepIndex !== -1) {
            insertIndex = targetStepIndex;
          } else {
            // Fallback if target step not found
            insertIndex = 0;
          }
        } else if (moveType === "below") {
          // For "Move Below", insert after the target step
          if (targetStepIndex !== -1) {
            insertIndex = targetStepIndex + 1;

            // If the target is the last step in order, just place it at the end
            if (targetStepSortedIndex === sortedTargetSteps.length - 1) {
              insertIndex = targetSteps.length;
            }
          } else {
            // Fallback if target step not found
            insertIndex = targetSteps.length;
          }
        }

        // Ensure index is within bounds
        insertIndex = Math.max(0, Math.min(insertIndex, targetSteps.length));

        // Create a copy of the step and insert it at the calculated position
        const newStepId = `${stepToMove.step_tree_node_id}_copy_${Date.now()}`;
        const stepCopy = {
          ...stepToMove,
          // Update the step ID to make it unique for the copy
          step_tree_node_id: newStepId,
          // Update sibling order to be after the target step
          sibling_order: insertIndex + 1,
        };
        targetSteps.splice(insertIndex, 0, stepCopy);

        // Copy all child elements associated with the original step
        const originalStepId = stepToMove.step_tree_node_id;

        // Copy rich texts
        if (richTexts[originalStepId]) {
          richTexts[originalStepId].forEach((richText) => {
            const newRichTextId = `${
              richText.rich_text_tree_node_id
            }_copy_${Date.now()}`;
            const richTextCopy = {
              ...richText,
              rich_text_tree_node_id: newRichTextId,
              step_id_ref: newStepId,
              step_tree_node_id: newStepId,
            };
            if (!updatedStepsBySection[targetSectionId + "_richTexts"]) {
              updatedStepsBySection[targetSectionId + "_richTexts"] = {};
            }
            if (
              !updatedStepsBySection[targetSectionId + "_richTexts"][newStepId]
            ) {
              updatedStepsBySection[targetSectionId + "_richTexts"][newStepId] =
                [];
            }
            updatedStepsBySection[targetSectionId + "_richTexts"][
              newStepId
            ].push(richTextCopy);
          });
        }

        // Copy forms
        if (forms[originalStepId]) {
          forms[originalStepId].forEach((form) => {
            const newFormId = `${form.form_tree_node_id}_copy_${Date.now()}`;
            const formCopy = {
              ...form,
              form_tree_node_id: newFormId,
              step_id_ref: newStepId,
              step_tree_node_id: newStepId,
            };
            if (!updatedStepsBySection[targetSectionId + "_forms"]) {
              updatedStepsBySection[targetSectionId + "_forms"] = {};
            }
            if (!updatedStepsBySection[targetSectionId + "_forms"][newStepId]) {
              updatedStepsBySection[targetSectionId + "_forms"][newStepId] = [];
            }
            updatedStepsBySection[targetSectionId + "_forms"][newStepId].push(
              formCopy
            );
          });
        }

        // Copy attachments
        if (attachments[originalStepId]) {
          attachments[originalStepId].forEach((attachment) => {
            const newAttachmentId = `${
              attachment.attachment_tree_node_id
            }_copy_${Date.now()}`;
            const attachmentCopy = {
              ...attachment,
              attachment_tree_node_id: newAttachmentId,
              step_id_ref: newStepId,
              step_tree_node_id: newStepId,
            };
            if (!updatedStepsBySection[targetSectionId + "_attachments"]) {
              updatedStepsBySection[targetSectionId + "_attachments"] = {};
            }
            if (
              !updatedStepsBySection[targetSectionId + "_attachments"][
                newStepId
              ]
            ) {
              updatedStepsBySection[targetSectionId + "_attachments"][
                newStepId
              ] = [];
            }
            updatedStepsBySection[targetSectionId + "_attachments"][
              newStepId
            ].push(attachmentCopy);
          });
        }

        // Copy canvases
        if (canvases[originalStepId]) {
          canvases[originalStepId].forEach((canvas) => {
            const newCanvasId = `${
              canvas.canvas_tree_node_id
            }_copy_${Date.now()}`;
            const canvasCopy = {
              ...canvas,
              canvas_tree_node_id: newCanvasId,
              step_id_ref: newStepId,
              step_tree_node_id: newStepId,
            };
            if (!updatedStepsBySection[targetSectionId + "_canvases"]) {
              updatedStepsBySection[targetSectionId + "_canvases"] = {};
            }
            if (
              !updatedStepsBySection[targetSectionId + "_canvases"][newStepId]
            ) {
              updatedStepsBySection[targetSectionId + "_canvases"][newStepId] =
                [];
            }
            updatedStepsBySection[targetSectionId + "_canvases"][
              newStepId
            ].push(canvasCopy);
          });
        }

        // Copy videos
        if (videos[originalStepId]) {
          videos[originalStepId].forEach((video) => {
            const newVideoId = `${video.video_tree_node_id}_copy_${Date.now()}`;
            const videoCopy = {
              ...video,
              video_tree_node_id: newVideoId,
              step_id_ref: newStepId,
              step_tree_node_id: newStepId,
            };
            if (!updatedStepsBySection[targetSectionId + "_videos"]) {
              updatedStepsBySection[targetSectionId + "_videos"] = {};
            }
            if (
              !updatedStepsBySection[targetSectionId + "_videos"][newStepId]
            ) {
              updatedStepsBySection[targetSectionId + "_videos"][newStepId] =
                [];
            }
            updatedStepsBySection[targetSectionId + "_videos"][newStepId].push(
              videoCopy
            );
          });
        }

        // Copy substeps
        if (substepsByStep[originalStepId]) {
          substepsByStep[originalStepId].forEach((substep) => {
            const newSubstepId = `${
              substep.sub_step_tree_node_id
            }_copy_${Date.now()}`;
            const substepCopy = {
              ...substep,
              sub_step_tree_node_id: newSubstepId,
              step_id_ref: newStepId,
              step_tree_node_id: newStepId,
            };
            if (!updatedStepsBySection[targetSectionId + "_substeps"]) {
              updatedStepsBySection[targetSectionId + "_substeps"] = {};
            }
            if (
              !updatedStepsBySection[targetSectionId + "_substeps"][newStepId]
            ) {
              updatedStepsBySection[targetSectionId + "_substeps"][newStepId] =
                [];
            }
            updatedStepsBySection[targetSectionId + "_substeps"][
              newStepId
            ].push(substepCopy);

            // Copy substep child elements
            const originalSubstepId = substep.sub_step_tree_node_id;

            // Copy substep rich texts
            if (richTexts[originalSubstepId]) {
              richTexts[originalSubstepId].forEach((richText) => {
                const newSubstepRichTextId = `${
                  richText.rich_text_tree_node_id
                }_copy_${Date.now()}`;
                const substepRichTextCopy = {
                  ...richText,
                  rich_text_tree_node_id: newSubstepRichTextId,
                  sub_step_id_ref: newSubstepId,
                  sub_step_tree_node_id: newSubstepId,
                };
                if (
                  !updatedStepsBySection[targetSectionId + "_substep_richTexts"]
                ) {
                  updatedStepsBySection[
                    targetSectionId + "_substep_richTexts"
                  ] = {};
                }
                if (
                  !updatedStepsBySection[
                    targetSectionId + "_substep_richTexts"
                  ][newSubstepId]
                ) {
                  updatedStepsBySection[targetSectionId + "_substep_richTexts"][
                    newSubstepId
                  ] = [];
                }
                updatedStepsBySection[targetSectionId + "_substep_richTexts"][
                  newSubstepId
                ].push(substepRichTextCopy);
              });
            }

            // Copy substep forms
            if (forms[originalSubstepId]) {
              forms[originalSubstepId].forEach((form) => {
                const newSubstepFormId = `${
                  form.form_tree_node_id
                }_copy_${Date.now()}`;
                const substepFormCopy = {
                  ...form,
                  form_tree_node_id: newSubstepFormId,
                  sub_step_id_ref: newSubstepId,
                  sub_step_tree_node_id: newSubstepId,
                };
                if (
                  !updatedStepsBySection[targetSectionId + "_substep_forms"]
                ) {
                  updatedStepsBySection[targetSectionId + "_substep_forms"] =
                    {};
                }
                if (
                  !updatedStepsBySection[targetSectionId + "_substep_forms"][
                    newSubstepId
                  ]
                ) {
                  updatedStepsBySection[targetSectionId + "_substep_forms"][
                    newSubstepId
                  ] = [];
                }
                updatedStepsBySection[targetSectionId + "_substep_forms"][
                  newSubstepId
                ].push(substepFormCopy);
              });
            }

            // Copy substep attachments
            if (attachments[originalSubstepId]) {
              attachments[originalSubstepId].forEach((attachment) => {
                const newSubstepAttachmentId = `${
                  attachment.attachment_tree_node_id
                }_copy_${Date.now()}`;
                const substepAttachmentCopy = {
                  ...attachment,
                  attachment_tree_node_id: newSubstepAttachmentId,
                  sub_step_id_ref: newSubstepId,
                  sub_step_tree_node_id: newSubstepId,
                };
                if (
                  !updatedStepsBySection[
                    targetSectionId + "_substep_attachments"
                  ]
                ) {
                  updatedStepsBySection[
                    targetSectionId + "_substep_attachments"
                  ] = {};
                }
                if (
                  !updatedStepsBySection[
                    targetSectionId + "_substep_attachments"
                  ][newSubstepId]
                ) {
                  updatedStepsBySection[
                    targetSectionId + "_substep_attachments"
                  ][newSubstepId] = [];
                }
                updatedStepsBySection[targetSectionId + "_substep_attachments"][
                  newSubstepId
                ].push(substepAttachmentCopy);
              });
            }

            // Copy substep canvases
            if (canvases[originalSubstepId]) {
              canvases[originalSubstepId].forEach((canvas) => {
                const newSubstepCanvasId = `${
                  canvas.canvas_tree_node_id
                }_copy_${Date.now()}`;
                const substepCanvasCopy = {
                  ...canvas,
                  canvas_tree_node_id: newSubstepCanvasId,
                  sub_step_id_ref: newSubstepId,
                  sub_step_tree_node_id: newSubstepId,
                };
                if (
                  !updatedStepsBySection[targetSectionId + "_substep_canvases"]
                ) {
                  updatedStepsBySection[targetSectionId + "_substep_canvases"] =
                    {};
                }
                if (
                  !updatedStepsBySection[targetSectionId + "_substep_canvases"][
                    newSubstepId
                  ]
                ) {
                  updatedStepsBySection[targetSectionId + "_substep_canvases"][
                    newSubstepId
                  ] = [];
                }
                updatedStepsBySection[targetSectionId + "_substep_canvases"][
                  newSubstepId
                ].push(substepCanvasCopy);
              });
            }

            // Copy substep videos
            if (videos[originalSubstepId]) {
              videos[originalSubstepId].forEach((video) => {
                const newSubstepVideoId = `${
                  video.video_tree_node_id
                }_copy_${Date.now()}`;
                const substepVideoCopy = {
                  ...video,
                  video_tree_node_id: newSubstepVideoId,
                  sub_step_id_ref: newSubstepId,
                  sub_step_tree_node_id: newSubstepId,
                };
                if (
                  !updatedStepsBySection[targetSectionId + "_substep_videos"]
                ) {
                  updatedStepsBySection[targetSectionId + "_substep_videos"] =
                    {};
                }
                if (
                  !updatedStepsBySection[targetSectionId + "_substep_videos"][
                    newSubstepId
                  ]
                ) {
                  updatedStepsBySection[targetSectionId + "_substep_videos"][
                    newSubstepId
                  ] = [];
                }
                updatedStepsBySection[targetSectionId + "_substep_videos"][
                  newSubstepId
                ].push(substepVideoCopy);
              });
            }
          });
        }

        // Update sibling orders of all steps after the insertion point
        targetSteps.forEach((step, idx) => {
          if (idx > insertIndex) {
            step.sibling_order = idx + 1;
            if (step.procedure_tree) {
              step.procedure_tree.sibling_order = idx + 1;
            }
          }
        });

        // Update the mappings - keep original steps in source section
        if (sourceSectionId !== targetSectionId) {
          updatedStepsBySection[targetSectionId] = targetSteps;
        } else {
          // If same section, update the target steps array
          updatedStepsBySection[targetSectionId] = targetSteps;
        }

        return updatedStepsBySection;
      } else {
        // Original move logic (not copy)
        // Remove step from source
        const [movedStep] = sourceSteps.splice(stepToMoveIndex, 1);

        // Make a copy of the target steps array
        let targetSteps =
          sourceSectionId === targetSectionId
            ? sourceSteps // If same section, use updated sourceSteps (after removal)
            : [...(updatedStepsBySection[targetSectionId] || [])];

        // Sort the target steps by sibling_order to understand their positions
        const sortedTargetSteps = [...targetSteps].sort((a, b) => {
          const aOrder = parseInt(
            a.sibling_order || a.procedure_tree?.sibling_order || 0
          );
          const bOrder = parseInt(
            b.sibling_order || b.procedure_tree?.sibling_order || 0
          );
          return aOrder - bOrder;
        });

        // Find the target step in the sorted array
        const targetStep = targetSteps.find(
          (step) => step.step_tree_node_id === targetSectionData.stepId
        );

        // Find the index of the target step in the original (unsorted) array
        const targetStepIndex = targetSteps.findIndex(
          (step) => step.step_tree_node_id === targetSectionData.stepId
        );

        // Find the position of the target step in the sorted array
        const targetStepSortedIndex = sortedTargetSteps.findIndex(
          (step) => step.step_tree_node_id === targetSectionData.stepId
        );

        // Calculate the insertion index based on move type
        let insertIndex;

        if (moveType === "into") {
          // Move to end of target section
          insertIndex = targetSteps.length;
        } else if (moveType === "up") {
          // For "Move Up", insert at the target step's position
          if (targetStepIndex !== -1) {
            insertIndex = targetStepIndex;
          } else {
            // Fallback if target step not found
            insertIndex = 0;
          }
        } else if (moveType === "below") {
          // For "Move Below", insert after the target step
          if (targetStepIndex !== -1) {
            insertIndex = targetStepIndex + 1;

            // If the target is the last step in order, just place it at the end
            if (targetStepSortedIndex === sortedTargetSteps.length - 1) {
              insertIndex = targetSteps.length;
            }
          } else {
            // Fallback if target step not found
            insertIndex = targetSteps.length;
          }
        }

        // Ensure index is within bounds
        insertIndex = Math.max(0, Math.min(insertIndex, targetSteps.length));

        // Insert the step at the calculated position
        targetSteps.splice(insertIndex, 0, movedStep);

        // Update the mappings
        updatedStepsBySection[sourceSectionId] = sourceSteps;
        if (sourceSectionId !== targetSectionId) {
          updatedStepsBySection[targetSectionId] = targetSteps;
        }

        return updatedStepsBySection;
      }
    } else {
      // Original section moving logic - just return the original for now
      // as we're only focusing on step movement
      const newStepsBySection = { ...stepsBySection };
      return newStepsBySection;
    }
  };

  const updated = getReorderedStepsBySection(
    stepsBySection,
    sections,
    sourceSectionIndex,
    targetSectionIndex,
    sourceStepIndex,
    location.state.targetStepIndex,
    moveType,
    location.state.movingStep,
    createCopy
  );

  // Helper function to get child elements (considering copied elements)
  const getChildElements = (stepId, sectionId, elementType) => {
    // First check if we have copied elements for this step
    const copiedElements = updated[sectionId + "_" + elementType]?.[stepId];
    if (copiedElements) {
      return copiedElements;
    }

    // Fallback to original Redux state
    switch (elementType) {
      case "richTexts":
        return richTexts[stepId] || [];
      case "forms":
        return forms[stepId] || [];
      case "attachments":
        return attachments[stepId] || [];
      case "canvases":
        return canvases[stepId] || [];
      case "videos":
        return videos[stepId] || [];
      case "substeps":
        return substepsByStep[stepId] || [];
      default:
        return [];
    }
  };

  // Helper function to get substep child elements
  const getSubstepChildElements = (substepId, sectionId, elementType) => {
    // First check if we have copied substep elements
    const copiedElements =
      updated[sectionId + "_substep_" + elementType]?.[substepId];
    if (copiedElements) {
      return copiedElements;
    }

    // Fallback to original Redux state
    switch (elementType) {
      case "richTexts":
        return richTexts[substepId] || [];
      case "forms":
        return forms[substepId] || [];
      case "attachments":
        return attachments[substepId] || [];
      case "canvases":
        return canvases[substepId] || [];
      case "videos":
        return videos[substepId] || [];
      default:
        return [];
    }
  };

  // Use the updated steps data for rendering
  const stepsToRender = selectedView === "original" ? stepsBySection : updated;

  return (
    <div style={{ position: "relative" }}>
      {/* Main Page Loader */}
      {isLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            <div
              style={{
                border: "4px solid #f3f3f3",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                animation: "spin 1s linear infinite",
                marginBottom: "10px",
              }}
            ></div>
            <div style={{ fontSize: "16px", color: "#333" }}>
              {createCopy ? "Copying" : "Moving"} step...
            </div>
          </div>
        </div>
      )}
      <ProcedureMoveAppBar
        selectedView={selectedView}
        showBackButton={false}
        setSelectedView={setSelectedView}
        title="Please Confirm the Changes"
        buttons={[{ label: "Confirm", onClick: handleConfirm }]}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          padding: "8px 20px",
          flexWrap: "wrap", // for responsiveness
        }}
      >
        {/* Checkbox + Title + Description */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              margin: 0,
            }}
          >
            {/* <input type="checkbox" disabled={true} checked={false} /> */}
            <span style={{ fontSize: "20px", fontWeight: 600 }}>
              {type} {partNumber}
            </span>
          </label>

          {/* Description */}
        </div>

        {/* Right-aligned section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            marginLeft: "auto",
          }}
        >
          <div>
            <strong>Status: </strong>
            <span style={{ color: "orange" }}>{location.state.status}</span>
            {/* <span style={{ color: "orange" }}>{status}</span> */}
          </div>

          <div>
            <strong>Revision: </strong>
            <span>{revision}</span>
          </div>
        </div>
      </div>
      <div style={{ padding: "20px" }}>
        <div style={{ marginTop: "20px" }}>
          {sortedSections
            .sort((a, b) => {
              const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
              const orderB = b.sibling_order || b.procedure_tree?.sibling_order;

              const orderCompare = compareSiblingOrders(orderA, orderB);
              if (orderCompare !== 0) return orderCompare;

              // Fallback: compare created_at
              const dateA = new Date(a.created_at);
              const dateB = new Date(b.created_at);
              return dateB - dateA; // latest comes first
            })
            .map((section, index) => {
              return (
                <div
                  key={section.section_tree_node_id}
                  style={{
                    margin: "20px 0",
                    clear: "both",
                  }}
                >
                  <h2 style={{ margin: 0, fontSize: "20px" }}>
                    Section {index + 1}
                  </h2>
                  <hr />
                  <hr />
                  <div>{section.section_name}</div>
                  <div>{section.short_description}</div>

                  {/* Steps for this section */}
                  {(stepsToRender[section.section_tree_node_id] || []).map(
                    (step, stepIndex) => {
                      const stepId = step.step_tree_node_id;
                      const sectionId = section.section_tree_node_id;
                      stepCounter++;
                      const stepsBeforeThisSection = previewSections
                        .slice(0, index)
                        .reduce(
                          (acc, s) =>
                            acc +
                            (stepsToRender[s.section_tree_node_id]?.length ||
                              0),
                          0
                        );
                      const globalStepNumber =
                        stepsBeforeThisSection + stepIndex + 1;

                      const substeps = substepsByStep[stepId] || [];

                      const substepLetter = String.fromCharCode(
                        65 + substeps.length
                      ); // A, B, C...

                      // Calculate media counters before navigation
                      const currentAttachmentCounter = attachmentCounter;
                      const currentVideoCounter = videoCounter;
                      const currentFormCounter = formCounter;
                      const currentCanvasCounter = canvasCounter;
                      const currentRichTextCounter = richTextCounter;

                      // Update counters for next step
                      //attachmentCounter += myAttachments.length;
                      // videoCounter += myVideos.length;
                      // formCounter += myForms.length;
                      // canvasCounter += myCanvases.length;
                      // richTextCounter += myRichTexts.length; // If you're adding one richText per step

                      richTextsCounter[stepId] = currentRichTextCounter;

                      const combinedItems = [
                        ...getChildElements(
                          stepId,
                          section.section_tree_node_id,
                          "richTexts"
                        ).map((item) => ({
                          ...item,
                          type: "richText",
                        })),
                        ...getChildElements(
                          stepId,
                          section.section_tree_node_id,
                          "forms"
                        ).map((item) => ({
                          ...item,
                          type: "form",
                        })),
                        ...getChildElements(
                          stepId,
                          section.section_tree_node_id,
                          "attachments"
                        ).map((item) => ({
                          ...item,
                          type: "attachment",
                        })),
                        ...getChildElements(
                          stepId,
                          section.section_tree_node_id,
                          "canvases"
                        ).map((item) => ({
                          ...item,
                          type: "canvas",
                        })),
                        ...getChildElements(
                          stepId,
                          section.section_tree_node_id,
                          "videos"
                        ).map((item) => ({
                          ...item,
                          type: "video",
                        })),
                      ]; // Filter out substeps or unwanted items (same as your current filter)
                      const filteredItems = combinedItems.filter(
                        (item) =>
                          !item.substep_id_ref && !item.sub_step_tree_node_id
                      );

                      filteredItems.sort((a, b) => {
                        const orderA =
                          a.sibling_order || a.procedure_tree?.sibling_order;
                        const orderB =
                          b.sibling_order || b.procedure_tree?.sibling_order;

                        const orderCompare = compareSiblingOrders(
                          orderA,
                          orderB
                        );
                        if (orderCompare !== 0) return orderCompare;

                        // Fallback: compare created_at
                        const dateA = new Date(a.created_at);
                        const dateB = new Date(b.created_at);
                        return dateB - dateA; // latest comes first
                      });
                      const allChildrenUnderStep = [
                        ...getChildElements(
                          stepId,
                          section.section_tree_node_id,
                          "richTexts"
                        ),
                        ...getChildElements(
                          stepId,
                          section.section_tree_node_id,
                          "forms"
                        ),
                        ...getChildElements(
                          stepId,
                          section.section_tree_node_id,
                          "attachments"
                        ),
                        ...getChildElements(
                          stepId,
                          section.section_tree_node_id,
                          "canvases"
                        ),
                        ...getChildElements(
                          stepId,
                          section.section_tree_node_id,
                          "videos"
                        ),
                        // Also optionally substeps, if they have sibling_order
                      ];

                      return (
                        <div
                          key={stepId}
                          style={{ marginTop: "20px", clear: "both" }}
                        >
                          <HeadingWithTreePath
                            title={`Step ${globalStepNumber}`}
                            treePathParams={{
                              sectionIndex: index,
                              stepIndex: globalStepNumber - 1,
                              substepIndex: undefined,
                              nodeType: undefined,
                              nodeIndex: undefined,
                            }}
                          />
                          <hr />
                          <div>
                            <p style={{ whiteSpace: 'pre-wrap' }}>{step.short_description || ""}</p>
                            <p>
                              <p>
                                {structuredClone(step.step_details)
                                  .sort(
                                    (a, b) =>
                                      new Date(a.created_at) -
                                      new Date(b.created_at)
                                  )
                                  .map((myStep) => {
                                    const partNumber =
                                      myStep.part_info?.part_number || "";
                                    const description =
                                      myStep.part_info?.description || "";
                                    const qtyText =
                                      myStep.qty && myStep.qty != 0
                                        ? `Qty ${myStep.qty}`
                                        : "";

                                    const partText = partNumber
                                      ? ` (${partNumber})`
                                      : "";

                                    return (
                                      <>
                                        {myStep.action}
                                        {partText}{" "}
                                        <RenderHtml content={description} />{" "}
                                        {qtyText}&nbsp;
                                      </>
                                    );
                                  })}
                              </p>
                            </p>

                            <h4>
                              {step.bom_table_title_info?.category_name || ""}
                            </h4>

                            <table className="table">
                              <thead>
                                <tr>
                                  <th>Type</th>
                                  <th>
                                    {
                                      step.part_number_column_title_ref_info
                                        ?.category_name
                                    }{" "}
                                  </th>
                                  <th>Description</th>
                                  <th>Qty Per</th>
                                  <th>Unit</th>
                                </tr>
                              </thead>

                              <tbody>
                                {step.parts_required.map((row) => (
                                  <tr>
                                    <td>{row.part_info.type_info.name}</td>
                                    <td>{row.part_info.part_number}</td>
                                    <td>
                                      <RenderHtml
                                        content={row.part_info.description}
                                      />{" "}
                                    </td>
                                    <td>{row.qty === 0 ? "" : row.qty}</td>
                                    <td>
                                      {row.part_info?.unit_info?.unit_name}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            {/* Step Content */}

                            {filteredItems.map((item, itemIndex) => {
                              if (
                                item.substep_id_ref ||
                                item.sub_step_tree_node_id
                              )
                                return null;
                              switch (item.type) {
                                case "richText": {
                                  const globalRichTextNumber =
                                    richTextCounter++; // ✅ use then increment
                                  // render richText block here
                                  const label = (
                                    <HeadingWithTreePath
                                      title={`Rich Text ${globalRichTextNumber}`}
                                      treePathParams={{
                                        sectionIndex: index,
                                        stepIndex: globalStepNumber - 1,
                                        substepIndex: undefined,
                                        nodeType: "richText",
                                        nodeIndex: globalRichTextNumber - 1,
                                      }}
                                    />
                                  );

                                  return (
                                    <div
                                      key={item.rich_text_tree_node_id}
                                      style={{
                                        marginTop: "20px",
                                        clear: "both",
                                      }}
                                    >
                                      <label
                                        style={{
                                          display: "flex",
                                          gap: "6px",
                                          cursor: "pointer",
                                          clear: "both",
                                        }}
                                      >
                                        {label}
                                      </label>

                                      <div
                                        style={{
                                          marginLeft: "5px",
                                          marginTop: "-10px",
                                        }}
                                      >
                                        {item.short_description}
                                      </div>
                                      <div
                                        style={{
                                          marginLeft: "4px",
                                          marginTop: "10px",
                                        }}
                                      >
                                        {item.category_data?.category_name}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "14px",
                                          marginLeft: "4px",
                                          marginTop: "5px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            marginTop: "-6px",
                                            clear: "both",
                                          }}
                                          dangerouslySetInnerHTML={{
                                            __html: item.rich_text_content,
                                          }}
                                        />
                                      </div>
                                    </div>
                                  );
                                }
                                case "video": {
                                  const globalVideoNumber = videoCounter++; // ✅ use then increment
                                  const label = (
                                    <HeadingWithTreePath
                                      title={`Video ${globalVideoNumber}`}
                                      treePathParams={{
                                        sectionIndex: index,
                                        stepIndex: globalStepNumber - 1,
                                        substepIndex: undefined,
                                        nodeType: "video",
                                        nodeIndex: globalVideoNumber - 1,
                                      }}
                                    />
                                  );

                                  return (
                                    <div
                                      key={label}
                                      style={{
                                        marginTop: "20px",
                                        clear: "both",
                                      }}
                                    >
                                      <label
                                        style={{
                                          display: "flex",
                                          gap: "6px",
                                          cursor: "pointer",
                                          clear: "both",
                                        }}
                                      >
                                        {label}
                                      </label>

                                      {renderVideo(item)}

                                      <div>{item.video_url}</div>

                                      <div>{item.short_description}</div>
                                    </div>
                                  );
                                }

                                case "form": {
                                  const globalFormNumber = formCounter++; // ✅ use then increment
                                  const label = (
                                    <HeadingWithTreePath
                                      title={`Form ${globalFormNumber}`}
                                      treePathParams={{
                                        sectionIndex: index,
                                        stepIndex: globalStepNumber - 1,
                                        substepIndex: undefined,
                                        nodeType: "form",
                                        nodeIndex: globalFormNumber - 1,
                                      }}
                                    />
                                  );
                                  // render form block here
                                  return (
                                    <div key={label} style={{ clear: "both" }}>
                                      <label
                                        style={{
                                          display: "flex",
                                          gap: "6px",
                                          cursor: "pointer",
                                          marginTop: "20px",
                                          clear: "both",
                                        }}
                                      >
                                        {label}
                                      </label>
                                      <div>{item.short_description}</div>
                                    </div>
                                  );
                                }
                                case "canvas": {
                                  const pages = getPages(item);

                                  const globalCanvasNumber = canvasCounter++; // ✅ use then increment
                                  const label = (
                                    <HeadingWithTreePath
                                      title={`Canvas ${globalCanvasNumber}`}
                                      treePathParams={{
                                        sectionIndex: index,
                                        stepIndex: globalStepNumber - 1,
                                        substepIndex: undefined,
                                        nodeType: "canvas",
                                        nodeIndex: globalCanvasNumber - 1,
                                      }}
                                    />
                                  );
                                  const shapes = item.shapes || [];

                                  return (
                                    <div
                                      key={label}
                                      style={{
                                        marginTop: "40px",
                                        clear: "both",
                                      }}
                                    >
                                      <label
                                        style={{
                                          display: "flex",
                                          gap: "6px",
                                          cursor: "pointer",
                                          clear: "both",
                                        }}
                                      >
                                        {label}
                                      </label>

                                      <div>{item.short_description}</div>

                                      {/* Display all pages vertically */}
                                      <CanvasDisplay pages={pages} />
                                    </div>
                                  );
                                }
                                case "attachment": {
                                  const attachmentList = item.attachments || [];
                                  if (attachmentList.length === 0) return null;

                                  //  const globalAttachmentNumber =
                                  //           attachmentCounter++; // ✅ use then increment
                                  const globalAttachmentNumber =
                                    attachmentCounter++; // ✅ Increment here per attachment
                                  const label = (
                                    <HeadingWithTreePath
                                      title={`Attachment ${globalAttachmentNumber}`}
                                      treePathParams={{
                                        sectionIndex: index,
                                        stepIndex: globalStepNumber - 1,
                                        substepIndex: undefined,
                                        nodeType: "attachment",
                                        nodeIndex: globalAttachmentNumber - 1,
                                      }}
                                    />
                                  );

                                  return (
                                    <div
                                      key={`attachment-${stepId}-${globalAttachmentNumber}`}
                                      style={{ clear: "both" }}
                                    >
                                      <label
                                        style={{
                                          display: "flex",
                                          gap: "6px",
                                          cursor: "pointer",
                                          marginTop: "20px",
                                          clear: "both",
                                        }}
                                      >
                                        {label}
                                      </label>
                                      <div>{item.short_description}</div>
                                      {attachmentList.map((attachment, i) => (
                                        <div
                                          key={`attachment-item-${stepId}-${globalAttachmentNumber}-${i}`}
                                          style={{ marginLeft: "20px" }}
                                        >
                                          {renderPreview(attachment)}
                                        </div>
                                      ))}
                                    </div>
                                  );
                                }
                                default:
                                  return null;
                              }
                            })}
                          </div>

                          {/* Substeps */}
                          {getChildElements(
                            stepId,
                            section.section_tree_node_id,
                            "substeps"
                          ).map((substep, substepIndex) => {
                            const substepId = substep.sub_step_tree_node_id;
                            const combinedItems = [
                              ...getSubstepChildElements(
                                substepId,
                                section.section_tree_node_id,
                                "richTexts"
                              ).map((item) => ({
                                ...item,
                                type: "richText",
                              })),
                              ...getSubstepChildElements(
                                substepId,
                                section.section_tree_node_id,
                                "forms"
                              ).map((item) => ({
                                ...item,
                                type: "form",
                              })),
                              ...getSubstepChildElements(
                                substepId,
                                section.section_tree_node_id,
                                "attachments"
                              ).map((item) => ({
                                ...item,
                                type: "attachment",
                              })),
                              ...getSubstepChildElements(
                                substepId,
                                section.section_tree_node_id,
                                "canvases"
                              ).map((item) => ({
                                ...item,
                                type: "canvas",
                              })),
                              ...getSubstepChildElements(
                                substepId,
                                section.section_tree_node_id,
                                "videos"
                              ).map((item) => ({
                                ...item,
                                type: "video",
                              })),
                            ]; // Filter out substeps or unwanted items (same as your current filter)
                            const filteredItems = combinedItems;

                            filteredItems.sort((a, b) => {
                              const orderA =
                                a.sibling_order ||
                                a.procedure_tree?.sibling_order;
                              const orderB =
                                b.sibling_order ||
                                b.procedure_tree?.sibling_order;

                              const orderCompare = compareSiblingOrders(
                                orderA,
                                orderB
                              );
                              if (orderCompare !== 0) return orderCompare;

                              // Fallback: compare created_at
                              const dateA = new Date(a.created_at);
                              const dateB = new Date(b.created_at);
                              return dateB - dateA; // latest comes first
                            });

                            // Global step number calculation based on the updated steps data
                            const stepsBeforeThisOne =
                              previewSections
                                .slice(0, index)
                                .reduce((sectionAcc, s) => {
                                  const steps =
                                    stepsToRender[s.section_tree_node_id] || [];
                                  return sectionAcc + steps.length;
                                }, 0) + stepIndex;

                            const globalStepNumber = stepsBeforeThisOne + 1;

                            // Final substep label like Substep 5A
                            const substepLetter = String.fromCharCode(
                              65 + substepIndex
                            ); // A, B, C, ...

                            const globalSubstepLabel = `Substep ${globalStepNumber}${substepLetter}`;

                            const globalRichTextNumber = richTextCounter; // ✅ use then increment
                            const globalAttachmentNumber = attachmentCounter;
                            const globalVideoNumber = videoCounter;
                            const globalCanvasNumber = canvasCounter;
                            const globalFormNumber = formCounter;

                            return (
                              <div
                                key={substepId}
                                style={{ marginTop: "20px", clear: "both" }}
                              >
                                <div>
                                  <label
                                    style={{
                                      display: "flex",
                                      gap: "8px",
                                      cursor: "pointer",
                                      clear: "both",
                                    }}
                                  >
                                    <HeadingWithTreePath
                                      title={globalSubstepLabel}
                                      treePathParams={{
                                        sectionIndex: index,
                                        stepIndex: globalStepNumber - 1,
                                        substepIndex: substepIndex,
                                        nodeType: undefined,
                                        nodeIndex: undefined,
                                      }}
                                    />
                                  </label>

                                  <div style={{ whiteSpace: 'pre-wrap' }}>{substep.short_description}</div>
                                </div>

                                {/* Substep Rich Texts */}
                                {filteredItems.map((item, itemIndex) => {
                                  switch (item.type) {
                                    case "richText": {
                                      const globalRichTextNumber =
                                        richTextCounter++; // ✅ use then increment
                                      // render richText block here
                                      const label = (
                                        <HeadingWithTreePath
                                          title={`Rich Text ${globalRichTextNumber}`}
                                          treePathParams={{
                                            sectionIndex: index,
                                            stepIndex: globalStepNumber - 1,
                                            substepIndex: substepIndex,
                                            nodeType: "richText",
                                            nodeIndex: globalRichTextNumber - 1,
                                          }}
                                        />
                                      );

                                      return (
                                        <div
                                          key={item.rich_text_tree_node_id}
                                          style={{
                                            marginTop: "20px",
                                            clear: "both",
                                          }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              cursor: "pointer",
                                              clear: "both",
                                            }}
                                          >
                                            {label}
                                          </label>

                                          <div
                                            style={{
                                              marginLeft: "5px",
                                              marginTop: "-10px",
                                              clear: "both",
                                            }}
                                          >
                                            {item.short_description}
                                          </div>
                                          <div
                                            style={{
                                              marginLeft: "4px",
                                              marginTop: "5px",
                                              clear: "both",
                                            }}
                                          >
                                            {item.category_data?.category_name}
                                          </div>
                                          <div
                                            style={{
                                              fontSize: "14px",
                                              marginLeft: "4px",
                                              marginTop: "5px",
                                              clear: "both",
                                            }}
                                          >
                                            <div
                                              style={{
                                                marginTop: "-6px",
                                                clear: "both",
                                              }}
                                              dangerouslySetInnerHTML={{
                                                __html: item.rich_text_content,
                                              }}
                                            />
                                          </div>
                                        </div>
                                      );
                                    }
                                    case "video": {
                                      const globalVideoNumber = videoCounter++; // ✅ use then increment
                                      const label = (
                                        <HeadingWithTreePath
                                          title={`Video ${globalVideoNumber}`}
                                          treePathParams={{
                                            sectionIndex: index,
                                            stepIndex: globalStepNumber - 1,
                                            substepIndex: substepIndex,
                                            nodeType: "video",
                                            nodeIndex: globalVideoNumber - 1,
                                          }}
                                        />
                                      );

                                      return (
                                        <div
                                          key={label}
                                          style={{
                                            marginTop: "20px",
                                            clear: "both",
                                          }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              cursor: "pointer",
                                              clear: "both",
                                            }}
                                          >
                                            {label}
                                          </label>

                                          {renderVideo(item)}

                                          <div>{item.video_url}</div>

                                          <div>{item.short_description}</div>
                                        </div>
                                      );
                                    }

                                    case "form": {
                                      const globalFormNumber = formCounter++; // ✅ use then increment
                                      const label = (
                                        <HeadingWithTreePath
                                          title={`Form ${globalFormNumber}`}
                                          treePathParams={{
                                            sectionIndex: index,
                                            stepIndex: globalStepNumber - 1,
                                            substepIndex: substepIndex,
                                            nodeType: "form",
                                            nodeIndex: globalFormNumber - 1,
                                          }}
                                        />
                                      );
                                      // render form block here
                                      return (
                                        <div
                                          key={label}
                                          style={{ clear: "both" }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              cursor: "pointer",
                                              marginTop: "20px",
                                              clear: "both",
                                            }}
                                          >
                                            {label}
                                          </label>
                                          <div>{item.short_description}</div>
                                        </div>
                                      );
                                    }
                                    case "canvas": {
                                      const pages = getPages(item);

                                      const globalCanvasNumber =
                                        canvasCounter++; // ✅ use then increment
                                      const label = (
                                        <HeadingWithTreePath
                                          title={`Canvas ${globalCanvasNumber}`}
                                          treePathParams={{
                                            sectionIndex: index,
                                            stepIndex: globalStepNumber - 1,
                                            substepIndex: substepIndex,
                                            nodeType: "canvas",
                                            nodeIndex: globalCanvasNumber - 1,
                                          }}
                                        />
                                      );
                                      const shapes = item.shapes || [];

                                      return (
                                        <div
                                          key={label}
                                          style={{
                                            marginTop: "40px",
                                            clear: "both",
                                          }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              cursor: "pointer",
                                              clear: "both",
                                            }}
                                          >
                                            {label}
                                          </label>

                                          <div>{item.short_description}</div>

                                          {/* Display all pages vertically */}
                                          <CanvasDisplay pages={pages} />
                                        </div>
                                      );
                                    }
                                    case "attachment": {
                                      const attachmentList =
                                        item.attachments || [];
                                      if (attachmentList.length === 0)
                                        return null;

                                      //  const globalAttachmentNumber =
                                      //           attachmentCounter++; // ✅ use then increment
                                      const globalAttachmentNumber =
                                        attachmentCounter++; // ✅ Increment here per attachment
                                      const label = (
                                        <HeadingWithTreePath
                                          title={`Attachment ${globalAttachmentNumber}`}
                                          treePathParams={{
                                            sectionIndex: index,
                                            stepIndex: globalStepNumber - 1,
                                            substepIndex: substepIndex,
                                            nodeType: "attachment",
                                            nodeIndex:
                                              globalAttachmentNumber - 1,
                                          }}
                                        />
                                      );

                                      return (
                                        <div
                                          key={`substep-attachment-${substepId}-${globalAttachmentNumber}`}
                                          style={{ clear: "both" }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              cursor: "pointer",
                                              marginTop: "20px",
                                              clear: "both",
                                            }}
                                          >
                                            {label}
                                          </label>
                                          <div>{item.short_description}</div>
                                          {attachmentList.map(
                                            (attachment, i) => (
                                              <div
                                                key={`substep-attachment-item-${substepId}-${globalAttachmentNumber}-${i}`}
                                                style={{
                                                  marginLeft: "20px",
                                                  clear: "both",
                                                }}
                                              >
                                                {renderPreview(attachment)}
                                              </div>
                                            )
                                          )}
                                        </div>
                                      );
                                    }
                                    default:
                                      return null;
                                  }
                                })}
                              </div>
                            );
                          })}
                        </div>
                      );
                    }
                  )}
                </div>
              );
            })}
        </div>
      </div>
      {/* Remove old loader position */}
      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content_addsection">
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <h4
                style={{
                  marginLeft: "23px",
                  fontSize: "18px",
                  color: "#333333",
                }}
              >
                {location.state.movingStep
                  ? (() => {
                      // Get the source section ID
                      const sourceSectionId = sortedSections.find(
                        (s) => s.section_tree_node_id === sourceSectionIndex
                      )?.section_tree_node_id;
                      // Get the target section ID
                      const targetSectionId =
                        sortedSections[targetSectionIndex]
                          ?.section_tree_node_id;
                      // Check if moving within the same section
                      const isSameSection = sourceSectionId === targetSectionId;

                      return `Confirm ${createCopy ? "Copy" : "Move"} of Step ${
                        sourceStepIndex + 1
                      } ${
                        moveType === "up"
                          ? "Above"
                          : moveType === "into"
                          ? ""
                          : "Below"
                      } ${
                        moveType === "into"
                          ? `${
                              //targetSectionIndex + 1}${
                              //  isSameSection ? " (Same Section)" : ""
                              ""
                            }`
                          : `Step ${
                              targetSectionData?.globalStepNumber ||
                              targetSectionData?.stepIndex + 1 ||
                              location.state.targetStepIndex + 1
                            }${isSameSection ? " Within Same Section" : ""}`
                      }
                      `;
                    })()
                  : `Confirm ${createCopy ? "Copy" : "Move"} of Section ${
                      sourceSectionIndex + 1
                    } ${moveType === "up" ? "Above" : "Below"} Section ${
                      targetSectionIndex + 1
                    }`}
              </h4>
              <button
                onClick={() => setModalOpen(false)}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "10px",
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                  color: "#666",
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div
              style={{ padding: "24px", marginTop: "-28px", color: "#333333" }}
            >
              <h4 style={{ fontSize: "14px" }}>
                For {type} {partNumber} - Rev {revision}
              </h4>

              <p style={{ fontSize: "14px", color: "#333333" }}>
                <strong>Editor:</strong>{" "}
                <span style={{ marginLeft: "10px" }}>
                  {localStorage.getItem("user_name")}
                </span>
              </p>

              <label
                className="modal-label"
                style={{ fontSize: "14px", color: "#333333" }}
              >
                Comment<span className="required">*</span>
              </label>
              <textarea
                className="modal-textarea"
                placeholder="Enter your comments here..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                style={{
                  width: "100%",
                  minHeight: "100px",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
                disabled={isModalLoading}
              ></textarea>
            </div>

            {/* Footer */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                padding: "16px 24px",
                borderTop: "1px solid #e0e0e0",
                marginTop: "-10px",
                position: "relative",
              }}
            >
              {isModalLoading && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    zIndex: 10,
                  }}
                >
                  <div
                    className="loader-spinner"
                    style={{
                      border: "4px solid #f3f3f3",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      animation: "spin 1s linear infinite",
                      marginRight: "10px",
                    }}
                  ></div>
                  <span>{createCopy ? "Copying..." : "Moving..."}</span>
                </div>
              )}
              <button
                onClick={() => setModalOpen(false)}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  background: "#6c757d",
                  color: "#e9ecef",
                  cursor: isModalLoading ? "not-allowed" : "pointer",
                }}
                disabled={isModalLoading}
              >
                Close
              </button>
              <button
                onClick={handleConfirmSave}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #007bff",
                  borderRadius: "4px",
                  background: "#007bff",
                  color: "white",
                  cursor: isModalLoading ? "not-allowed" : "pointer",
                  opacity: isModalLoading ? 0.7 : 1,
                }}
                disabled={isModalLoading}
              >
                {isModalLoading
                  ? createCopy
                    ? "Copying..."
                    : "Moving..."
                  : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcedureStepMovePreview;
